/**
 * AuditList.js
 *
 * Main dashboard listing all CBAM audits the current user can access.
 *
 *   AppAdmin    -> all audits in their tenant (+ can create / rename / delete)
 *   AppUser     -> all audits in their tenant (read-only metadata; can edit
 *                  form data via row click). NO create / rename / delete.
 *   SuperAdmin  -> NO audit access (redirected to /tenant-management)
 *   Verifier    -> NO audit access in MR 1 (redirected to /verifier in App.js)
 *
 * Click a row -> navigate to /form-a/:auditId (existing flow continues).
 *
 * Chunk 1: read-only list.
 * Chunk 2: AppAdmin creates audits via CreateAuditDialog.
 * Chunk 3: AppAdmin renames/deletes audits.
 * Chunk 4: AppUser sees full tenant list (no per-user filtering);
 *          assignedUsers concept dropped.
 *
 * Visual refresh: row-cards + tinted icon badges, matching the color
 * language already used in the sidebar and form-progress chips elsewhere
 * in the app (see TOKENS below).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, CircularProgress, Alert,
  Tooltip, Button, IconButton, useTheme, alpha, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import {
  collection, query, where, getDocs, orderBy,
} from 'firebase/firestore';
import { firestore as db } from './firebase.config';
import { useAuth } from '../auth/AuthContext';
// CreateAuditDialog no longer imported — "New Audit" now navigates to
// /form-a/new and creation happens inside Form A itself (Setup tab).
import RenameAuditDialog from './RenameAuditDialog';
import DeleteAuditDialog from './DeleteAuditDialog';
import DuplicateAuditDialog from './DuplicateAuditDialog';

// ── Design tokens — pulled from the app's existing header gradient &
//    sidebar icon-badge treatment, not a fresh palette. ─────────────────────
const TOKENS = {
  gradient: 'linear-gradient(135deg, #7C6EF2 0%, #4A90E2 100%)',
  pageBg: '#F4F6FB',
  ink: '#101B3D',
};

const STATUS_STYLES = {
  draft:     { label: 'Draft',     text: '#5B6472', bg: '#EEF1F6', dot: '#94A3B8' },
  submitted: { label: 'Submitted', text: '#B7791F', bg: '#FFF4E0', dot: '#F59E0B' },
  published: { label: 'Published', text: '#1E9E63', bg: '#E4F8EE', dot: '#22B573' },
};

const FREQUENCY_STYLES = {
  Annual:    { text: '#3D6FD6', bg: '#E9F0FE' },
  Quarterly: { text: '#B7791F', bg: '#FFF4E0' },
};

// ── Relative time helper ─────────────────────────────────────────────────────
const relativeTime = (firestoreDate) => {
  if (!firestoreDate) return '—';
  const date = firestoreDate.toDate ? firestoreDate.toDate() : new Date(firestoreDate);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60)         return 'just now';
  if (seconds < 3600)       return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400)      return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400 * 7)  return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString('en-GB');
};

// Fixed grid so header labels and row cells line up.
//  Actions col widened from 96px → 128px to fit the Duplicate icon
//  alongside Edit and Delete.
const gridCols = (withActions) =>
  withActions ? '2.4fr 1fr 1fr 1.2fr 1.6fr 128px' : '2.4fr 1fr 1fr 1.2fr 1.6fr';

const AuditList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    isSuperAdmin,
    isAppAdmin,
    isAppUser,
    isVerifier,
    tenantId,
    tenantName,
    loading: authLoading,
  } = useAuth();

  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [renameDialogAudit, setRenameDialogAudit] = useState(null);
  const [deleteDialogAudit, setDeleteDialogAudit] = useState(null);
  const [duplicateDialogAudit, setDuplicateDialogAudit] = useState(null);

  // ── Fetch audits based on role ─────────────────────────────────────────────
  // AppAdmin and AppUser see the SAME list: all audits in their tenant.
  // The only difference between them is the Actions column visibility.
  const fetchAudits = useCallback(async () => {
    if (!isAppAdmin && !isAppUser) {
      setAudits([]);
      setLoading(false);
      return;
    }
    if (!tenantId) {
      setError('No tenant assigned to your account.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const q = query(
        collection(db, 'auditReports'),
        where('tenantId', '==', tenantId),
        orderBy('updatedAt', 'desc')
      );

      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAudits(list);
    } catch (err) {
      console.error('Error loading audits:', err);
      setError(
        'Could not load audits. ' +
        (err?.message || '') +
        (err?.code === 'failed-precondition'
          ? ' (You may need to create a Firestore composite index — check the console for the link.)'
          : '')
      );
    } finally {
      setLoading(false);
    }
  }, [isAppAdmin, isAppUser, tenantId]);

  useEffect(() => {
    if (authLoading) return;
    fetchAudits();
  }, [authLoading, fetchAudits]);

  // ── Role-based redirects ──────────────────────────────────────────────────
  if (!authLoading && isSuperAdmin) {
    return <Navigate to="/tenant-management" replace />;
  }
  if (!authLoading && isVerifier) {
    return <Navigate to="/verifier" replace />;
  }

  // ── Open an audit ──────────────────────────────────────────────────────────
  const openAudit = (auditId) => {
    navigate(`/dashboard/${auditId}`);
  };

  //  "New Audit" → Form A in creation mode (skip dashboard).
  //  No Firestore write here; Form A handles it on "Save & Continue".
  const startNewAudit = () => {
    navigate('/form-a/new');
  };

  // ── After rename, optimistic in-place update ───────────────────────────────
  const handleAuditRenamed = (auditId, { name, description }) => {
    setRenameDialogAudit(null);
    setAudits((prev) =>
      prev.map((a) => (a.id === auditId ? { ...a, name, description } : a))
    );
  };

  // ── After delete, drop from local list ─────────────────────────────────────
  const handleAuditDeleted = (deletedId) => {
    setDeleteDialogAudit(null);
    setAudits((prev) => prev.filter((a) => a.id !== deletedId));
  };

  //  After duplicate — re-fetch the list so the new audit shows up at top
  //  (audits are ordered by updatedAt desc, and the duplicate's timestamp
  //  is fresh, so it lands first).
  const handleAuditDuplicated = () => {
    setDuplicateDialogAudit(null);
    fetchAudits();
  };

  // ── Render guards ──────────────────────────────────────────────────────────
  if (authLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: '#7C6EF2' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // ── Real counts for the stat strip (data, not decoration) ───────────────────
  const counts = audits.reduce(
    (acc, a) => {
      const s = (a.status === 'in_progress' ? 'draft' : a.status) || 'draft';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { draft: 0, submitted: 0, published: 0 }
  );

  return (
    <Box sx={{ bgcolor: TOKENS.pageBg, minHeight: '100%', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                background: TOKENS.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5,
              }}
            >
             CBAM Reports
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ color: alpha(TOKENS.ink, 0.55) }}>
                {tenantName ? `${tenantName} · ` : ''}{audits.length} total
              </Typography>
              {audits.length > 0 && (
                <Stack direction="row" spacing={1}>
                  {counts.draft > 0 && (
                    <StatPill color={STATUS_STYLES.draft} label={`${counts.draft} draft`} />
                  )}
                  {counts.submitted > 0 && (
                    <StatPill color={STATUS_STYLES.submitted} label={`${counts.submitted} submitted`} />
                  )}
                  {counts.published > 0 && (
                    <StatPill color={STATUS_STYLES.published} label={`${counts.published} published`} />
                  )}
                </Stack>
              )}
            </Stack>
          </Box>

          {isAppAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={startNewAudit}
              sx={{
                minWidth: 160,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                py: 1,
                px: 2.5,
                background: TOKENS.gradient,
                boxShadow: '0 8px 20px rgba(74, 108, 226, 0.35)',
                '&:hover': {
                  background: TOKENS.gradient,
                  boxShadow: '0 10px 24px rgba(74, 108, 226, 0.45)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.15s ease',
              }}
            >
              New Report
            </Button>
          )}
        </Box>

        {audits.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 4,
              border: `1px solid ${alpha(TOKENS.ink, 0.08)}`,
              bgcolor: '#fff',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: alpha('#7C6EF2', 0.1),
              }}
            >
              <InboxRoundedIcon sx={{ fontSize: 30, color: '#7C6EF2' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: TOKENS.ink, mb: 1 }}>
              No audits yet
            </Typography>
            <Typography variant="body2" sx={{ color: alpha(TOKENS.ink, 0.55), mb: isAppAdmin ? 3 : 0 }}>
              {isAppAdmin
                ? 'No audits have been created for your tenant. Start your first one below.'
                : 'No audits have been created yet. Ask your tenant administrator to create one.'}
            </Typography>
            {isAppAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={startNewAudit}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  background: TOKENS.gradient,
                  boxShadow: '0 8px 20px rgba(74, 108, 226, 0.35)',
                }}
              >
                New
              </Button>
            )}
          </Paper>
        ) : (
          <Box>
            {/* Column labels — uppercase / letter-spaced, matching the form field label style */}
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: gridCols(isAppAdmin),
                gap: 2,
                px: 3,
                mb: 1,
              }}
            >
              {['Audit Name', 'Frequency', 'Status', 'Last Modified', 'Created By'].map((label) => (
                <Typography
                  key={label}
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: alpha(TOKENS.ink, 0.4),
                  }}
                >
                  {label}
                </Typography>
              ))}
              {isAppAdmin && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: alpha(TOKENS.ink, 0.4),
                    textAlign: 'center',
                  }}
                >
                  Actions
                </Typography>
              )}
            </Box>

            <Stack spacing={1.5}>
              {audits.map((audit) => {
                const displayName =
                  audit.name ||
                  audit.installationNameEnglish ||
                  audit.installationName ||
                  `Audit ${audit.id?.slice(0, 8) || ''}`;

                const statusKey = (audit.status === 'in_progress' ? 'draft' : audit.status) || 'draft';
                const status = STATUS_STYLES[statusKey] || STATUS_STYLES.draft;
                const freq = FREQUENCY_STYLES[audit.reportingFrequency];

                return (
                  <Paper
                    key={audit.id}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(TOKENS.ink, 0.07)}`,
                      borderLeft: `4px solid ${status.dot}`,
                      bgcolor: '#fff',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 10px 24px ${alpha(TOKENS.ink, 0.08)}`,
                        borderColor: alpha(status.dot, 0.4),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: gridCols(isAppAdmin) },
                        gap: 2,
                        alignItems: 'center',
                        px: 3,
                        py: 2,
                      }}
                    >
                      {/* Name + icon badge */}
                      <Box
                        onClick={() => openAudit(audit.id)}
                        sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', cursor: 'pointer', minWidth: 0 }}
                      >
                        <Box
                          sx={{
                            flexShrink: 0,
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(status.dot, 0.12),
                          }}
                        >
                          <DescriptionRoundedIcon sx={{ fontSize: 20, color: status.dot }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: TOKENS.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {displayName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: alpha(TOKENS.ink, 0.4) }}>
                            ID: {audit.id?.slice(0, 8)}…
                          </Typography>
                          {audit.description && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.25,
                                fontStyle: 'italic',
                                color: alpha(TOKENS.ink, 0.5),
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {audit.description.length > 80
                                ? `${audit.description.slice(0, 80)}…`
                                : audit.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Frequency */}
                      <Box onClick={() => openAudit(audit.id)} sx={{ cursor: 'pointer' }}>
                        {freq ? (
                          <Pill text={freq.text} bg={freq.bg} label={audit.reportingFrequency} />
                        ) : (
                          <Typography variant="caption" sx={{ color: alpha(TOKENS.ink, 0.35) }}>—</Typography>
                        )}
                      </Box>

                      {/* Status */}
                      <Box onClick={() => openAudit(audit.id)} sx={{ cursor: 'pointer' }}>
                        <Pill text={status.text} bg={status.bg} label={status.label} dot={status.dot} />
                      </Box>

                      {/* Last modified */}
                      <Box onClick={() => openAudit(audit.id)} sx={{ cursor: 'pointer' }}>
                        <Tooltip
                          title={
                            audit.updatedAt?.toDate
                              ? audit.updatedAt.toDate().toLocaleString('en-GB')
                              : ''
                          }
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AccessTimeRoundedIcon sx={{ fontSize: 15, color: alpha(TOKENS.ink, 0.35) }} />
                            <Typography variant="body2" sx={{ color: alpha(TOKENS.ink, 0.65) }}>
                              {relativeTime(audit.updatedAt)}
                            </Typography>
                          </Stack>
                        </Tooltip>
                      </Box>

                      {/* Created by */}
                      <Box onClick={() => openAudit(audit.id)} sx={{ cursor: 'pointer', minWidth: 0 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <PersonOutlineRoundedIcon sx={{ fontSize: 15, color: alpha(TOKENS.ink, 0.35), flexShrink: 0 }} />
                          <Typography
                            variant="caption"
                            sx={{ color: alpha(TOKENS.ink, 0.6), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {audit.createdBy || '—'}
                          </Typography>
                        </Stack>
                      </Box>

                      {/* Actions */}
                      {isAppAdmin && (
                        <Box
                          onClick={(e) => e.stopPropagation()}
                          sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}
                        >
                          <Tooltip title="Edit name / description">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenameDialogAudit(audit);
                              }}
                              sx={{
                                color: '#4A90E2',
                                bgcolor: alpha('#4A90E2', 0.08),
                                '&:hover': { bgcolor: alpha('#4A90E2', 0.16) },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {/*  Duplicate — opens DuplicateAuditDialog */}
                          <Tooltip title="Duplicate audit">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDuplicateDialogAudit(audit);
                              }}
                              sx={{
                                color: '#7C6EF2',
                                bgcolor: alpha('#7C6EF2', 0.08),
                                '&:hover': { bgcolor: alpha('#7C6EF2', 0.16) },
                              }}
                            >
                              <ContentCopyRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete audit">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteDialogAudit(audit);
                              }}
                              sx={{
                                color: '#EF4444',
                                bgcolor: alpha('#EF4444', 0.08),
                                '&:hover': { bgcolor: alpha('#EF4444', 0.16) },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Dialogs — rename & delete only. Create is now a full-page flow via Form A. */}
        <RenameAuditDialog
          open={!!renameDialogAudit}
          audit={renameDialogAudit}
          onClose={() => setRenameDialogAudit(null)}
          onRenamed={handleAuditRenamed}
        />
        <DeleteAuditDialog
          open={!!deleteDialogAudit}
          audit={deleteDialogAudit}
          onClose={() => setDeleteDialogAudit(null)}
          onDeleted={handleAuditDeleted}
        />
        <DuplicateAuditDialog
          open={!!duplicateDialogAudit}
          sourceAudit={duplicateDialogAudit}
          onClose={() => setDuplicateDialogAudit(null)}
          onDuplicated={handleAuditDuplicated}
        />
      </Container>
    </Box>
  );
};

// ── Small pill/chip used for frequency + status (echoes the "0%" progress
//    badges used on the form tabs) ───────────────────────────────────────────
const Pill = ({ text, bg, label, dot }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.25,
      py: 0.5,
      borderRadius: 999,
      bgcolor: bg,
    }}
  >
    {dot && (
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />
    )}
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.02em', color: text }}
    >
      {label}
    </Typography>
  </Box>
);

// Header stat pill (draft / submitted / published counts)
const StatPill = ({ color, label }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.6,
      px: 1,
      py: 0.35,
      borderRadius: 999,
      bgcolor: color.bg,
    }}
  >
    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color.dot }} />
    <Typography variant="caption" sx={{ fontWeight: 600, color: color.text }}>
      {label}
    </Typography>
  </Box>
);

export default AuditList;